import React, { useEffect, useState } from "react";
import { Facebook, Instagram, MessageCircle, Music } from "lucide-react";
import { useTranslation } from "react-i18next";
import managerLogo from "../assets/manager_custom_logo.png";
import { getLocationSettings } from "../api/settings";
import { formatAddress } from "../utils/utils";
import { Address, LocationSettings } from "../types";

interface FooterProps {
  onLegalPageOpen: (type: "privacy" | "terms" | "refund" | "legal") => void;
  setCurrentPage: (page: string) => void;
}

const Footer: React.FC<FooterProps> = ({
  onLegalPageOpen,
  setCurrentPage,
  locationSettings,
}) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  // const socialLinks = [
  //   {
  //     name: "Facebook",
  //     icon: Facebook,
  //     url: "https://facebook.com/managerfood",
  //     color: "hover:text-blue-400",
  //   },
  //   {
  //     name: "Instagram",
  //     icon: Instagram,
  //     url: "https://instagram.com/managerfood",
  //     color: "hover:text-pink-400",
  //   },
  //   {
  //     name: "WhatsApp",
  //     icon: MessageCircle,
  //     url: "https://wa.me/237123456789",
  //     color: "hover:text-green-400",
  //   },
  //   {
  //     name: "TikTok",
  //     icon: Music,
  //     url: "https://tiktok.com/@managerfood",
  //     color: "hover:text-purple-400",
  //   },
  // ];

  const socialLinks = [];

  const legalLinks = [
    { name: t("footer.privacy"), type: "privacy" as const },
    { name: t("footer.terms"), type: "terms" as const },
    { name: t("footer.refund"), type: "refund" as const },
    { name: t("footer.legal"), type: "legal" as const },
  ];

  return (
    <footer className="bg-black text-white mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* À propos */}
          <div className="space-y-4">
            <div className="flex items-center cursor-pointer mb-4">
              <img
                src={managerLogo}
                alt={t("header.appName")}
                className="h-20"
              />
            </div>
            <h3 className="text-lg font-semibold text-white mb-4">
              {t("footer.aboutTitle")}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              {t("footer.aboutDescription")}
            </p>
            <p className="text-gray-300 text-sm">{t("footer.aboutSince")}</p>
          </div>

          {/* Liens utiles */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              {t("footer.linksTitle")}
            </h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => setCurrentPage("about")}
                  className="text-gray-300 text-sm hover:text-orange-500 transition-colors duration-200"
                >
                  {t("footer.aboutUs")}
                </button>
              </li>
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => onLegalPageOpen(link.type)}
                    className="text-gray-300 text-sm hover:text-orange-500 transition-colors duration-200"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Réseaux sociaux */}
          <div className="space-y-4">
            {/* <h3 className="text-lg font-semibold text-white mb-4">
              {t("footer.socialTitle")}
            </h3>
            <p className="text-gray-300 text-sm mb-4">
              {t("footer.socialDescription")}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-300 ${social.color} transition-all duration-200 hover:bg-gray-700 hover:scale-110`}
                    aria-label={social.name}
                  >
                    <IconComponent className="w-5 h-5" strokeWidth={1.5} />
                  </a>
                );
              })}
            </div> */}

            {/* Contact info */}
            <h3 className="text-lg font-semibold text-white mb-4">
              {t("footer.contactTitle")}
            </h3>
            <div className="mt-6 space-y-2">
              <p className="text-gray-300 text-sm">📞 +1 581-580-5573</p>
              <p className="text-gray-300 text-sm">
                📧 managerfood25@gmail.com
              </p>
              <p className="text-gray-300 text-sm">
                {`📍 ${
                  locationSettings !== null
                    ? formatAddress({
                        id: locationSettings.id,
                        street: locationSettings.street,
                        city: locationSettings.city,
                        country: locationSettings.country,
                        zipcode: locationSettings.postalCode,
                        latitude: locationSettings.latitude,
                        longitude: locationSettings.longitude,
                        province: locationSettings.province,
                        provinceAbbrev: locationSettings.provinceAbbrev,
                      } as Address)
                    : ""
                }`}
              </p>
            </div>
          </div>
        </div>

        {/* Ligne de séparation */}
        <div className="border-t border-gray-800 mt-8 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Manager Food. {t("footer.rights")}
            </p>

            <div className="flex items-center space-x-2 text-gray-400 text-sm">
              <span>{t("footer.createdBy")}</span>
              <a
                href="https://cgweb.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-500 hover:text-orange-400 font-medium transition-colors duration-200"
              >
                Canada Global Web
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
